import type { Lanuages } from "@/lib/types";

export const SUPPORTED_LOCALES = ["en", "am", "ti", "om"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "en";

export const ETHIOPIC_LOCALES = ["am", "ti"] as const;

export type AppLanguage = {
  code: string;
  name: string;
};

export function normalizeLocaleCode(code?: string | null): string {
  return String(code || "").trim().toLowerCase();
}

export function isSupportedLocale(code?: string | null): code is SupportedLocale {
  const normalized = normalizeLocaleCode(code);
  return SUPPORTED_LOCALES.includes(normalized as SupportedLocale);
}

export function isEthiopicLocale(code?: string | null): boolean {
  const normalized = normalizeLocaleCode(code);
  return ETHIOPIC_LOCALES.includes(normalized as (typeof ETHIOPIC_LOCALES)[number]);
}

export function resolveLocale(
  code?: string | null,
  fallback: SupportedLocale = DEFAULT_LOCALE
): SupportedLocale {
  return isSupportedLocale(code) ? (normalizeLocaleCode(code) as SupportedLocale) : fallback;
}

export function getLocaleFromCookie(cookie: string | undefined): SupportedLocale | null {
  if (!cookie) return null;

  const match = cookie.match(/(?:^|;\s*)locale=([^;]+)/);
  if (!match) return null;

  const parsed = decodeURIComponent(match[1]);
  return isSupportedLocale(parsed) ? resolveLocale(parsed) : null;
}

export function normalizeLanguages(items: Lanuages[] | null | undefined): AppLanguage[] {
  const source = Array.isArray(items) ? items : [];
  const seen = new Set<string>();
  const normalized: AppLanguage[] = [];

  for (const item of source) {
    const code = normalizeLocaleCode(item.code);
    const name = String(item.name || "").trim();
    if (!code || !name || seen.has(code)) continue;
    seen.add(code);
    normalized.push({ code, name });
  }

  return normalized;
}

export function getLocaleFromPath(pathname: string): SupportedLocale | null {
  const firstSegment = pathname.split("/").filter(Boolean)[0];
  if (!firstSegment) return null;

  return isSupportedLocale(firstSegment) ? resolveLocale(firstSegment) : null;
}

export function replaceLocaleInPath(pathname: string, locale: string): string {
  const nextLocale = resolveLocale(locale);
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return `/${nextLocale}`;
  }

  if (isSupportedLocale(segments[0])) {
    segments[0] = nextLocale;
    return `/${segments.join("/")}`;
  }

  return `/${nextLocale}/${segments.join("/")}`;
}
